// NOTE: This file focuses on parsing the low entropy client hints
// from the user agent. Even though UA has more information than that, it's not
// good to try to parse it out and make websites that depend on it since the
// web is moving away from that.
// I.e. do you need to know the architecture on the android tv the page is
// being viewed on for your site to function properly?
//
// NOTE 2: Existing packages were considered for this. The best one by far
// seems to be "ua-parser-js" which supports client hints, auto-detection
// on the client, parsing server-side headers etc. However it's 6kb zipped
// because it supports every single device known to mankind, which is a lot.

import type { BrowserBrand, LowEntropyBrowserHints, Platform } from '..';
import { parseMajorVersion } from './parseMajorVersion';

/** Matcher for user agent version building blocks like AppleWebKit/605.1.15 */
const browserVersionRegex = /([a-zA-Z0-9]+)\/([0-9.]+)/g;

const isMobileRegex = /Mobile/;

/**
 * Supported browser brands.
 *
 * This covers both the User Agent string string variants a well as secure client hint user agent
 * ones.
 *
 * See https://wiki.sc-corp.net/pages/viewpage.action?pageId=157868735 for the choice of the ones
 * that are supported as distinct browsers. The others have to use fallbacks or list an approximate
 * compatibility (which they do).
 */
const supportedBrowserBrands: Record<string, BrowserBrand> = {
  Firefox: 'Firefox',
  Safari: 'Safari',
  Chromium: 'Chromium',
  'Google Chrome': 'Chrome',
  Chrome: 'Chrome',
  Edge: 'Edge',
  Edg: 'Edge',
  'Microsoft Edge': 'Edge',
  CriOS: 'Chrome', // Chrome on iOS
  FxiOS: 'Firefox', // Firefox on iOS
  OPR: 'Opera',
  Opera: 'Opera',
  Presto: 'Opera Mini', // Opera Mini still uses Presto and keeps getting updates.
};

/**
 * Parses a string w/ the brand of a browser into a standard set.
 *
 * Note that this implementation on purpose DOES NOT differentiate between different versions of a
 * brand. I.e. "Firefox Mobile" === "Firefox" and "Opera Mini" === "Opera". It is expected that the
 * "isMobile" client hint is used to make the distinction where mobile vs desktop matters, and the
 * "save-data" hint is used to determine how much brandwidth the device has.
 */
export function parseBrowserBrand(brand: string): BrowserBrand {
  return supportedBrowserBrands[brand] ?? 'Unknown';
}

const platformRegex = /(\([^)]+\))/;

/** Returns a standard platform account for the common ones we receive traffic from. */
export function parsePlatform(platform?: string): Platform | undefined {
  if (!platform) return undefined;
  if (/iOS|iPhone/.test(platform)) return 'iOS'; // Has to be before macOS test.
  if (/macOS|Mac OS|AppleTV/.test(platform)) return 'macOS';
  if (/Android|CrKey/.test(platform)) return 'Android'; // Has to be before Linux.
  if (/Windows/.test(platform)) return 'Windows';
  if (/Linux|Roku|PlayStation/.test(platform)) return 'Linux';
  if (/CrOS/.test(platform)) return 'Chrome OS';
  return undefined;
}

/** WebView browser appends the "wv" at the end of the platform part of the user agent. */
const androidWebViewRegex = /; wv\)/;

const appleRegex = /iPhone|iPad/;

/** Regex for browser that need to be promoted as the first brand. */
const promoteBrandNameRegex = /Edge|Opera|Opera Mini/;

/**
 * Parses the browser brand and versions from the user agent.
 *
 * Handles weird cases like "Version/X Brand/Y" actually means "Brand/X"
 */
function parseBrowserBrandsFromUa(
  userAgent?: string
): { brand: BrowserBrand; majorVersion: number }[] {
  if (!userAgent) return [];
  const matchArray = userAgent.matchAll(browserVersionRegex);

  const isApple = appleRegex.test(userAgent);
  const _isAndroidWebView = androidWebViewRegex.test(userAgent);
  let appleWebViewVersion: number | undefined;
  const result = [] as { brand: BrowserBrand; majorVersion: number }[];

  for (const [, brand, version] of matchArray) {
    // Skip type of mobile device. I.e. Mobile/15A372.
    if (brand === 'Mobile' && isApple) {
      appleWebViewVersion = parseMajorVersion(version);
      continue;
    }

    const mappedBrand = supportedBrowserBrands[brand];
    if (!mappedBrand) continue;
    let majorVersion = parseMajorVersion(version);

    if (mappedBrand === 'Safari') {
      // If we're looking for "Safari" and have found it, can stop looking
      appleWebViewVersion = undefined;

      // Account for Version/X Safari/Y => Safari/X
      // And Version/X Mobile/Blah Safari/Y => Safari/X
      const safariVersion = /Version\/([0-9.]*)( Mobile\/[A-Z0-9]+)? Safari/.exec(userAgent);
      if (safariVersion) majorVersion = parseMajorVersion(safariVersion[1]);

      // Skip webkit subversion match. I.e. Safari/537 which is a match for AppleWebKit/537
      if (majorVersion > 530) continue;
    }

    // Support Opera Mini
    if (mappedBrand === 'Opera Mini') {
      const operaMiniVersion = /Opera Mini\/([0-9.]*)/.exec(userAgent);
      if (operaMiniVersion) majorVersion = parseMajorVersion(operaMiniVersion[1]);
    }

    // Some brands put their version last, so they need to be promoted to the
    // from to be consistent with the Client Hints api.
    // I.e. 'Chrome/123.0.0 Safari/123.0.0 Edg/123.0.0` but it's Edge.
    if (promoteBrandNameRegex.test(mappedBrand)) {
      result.unshift({ brand: mappedBrand, majorVersion });
    } else {
      result.push({ brand: mappedBrand, majorVersion });
    }
  }

  // If we're on Apple and have found a mobile version, then that's Safari Webview.
  // Need to make sure it's recorded if there isn't another browser that's being
  // used like ChriOS (Chrome on iOS).
  if (appleWebViewVersion && result.length === 0) {
    result.push({
      brand: 'Safari',
      majorVersion: appleWebViewVersion,
    });
  }

  return result;
}

/**
 * Parses out low entropy client hints from a user-agent.
 *
 * Useful tool is: https://51degrees.com/developers/user-agent-tester
 */
export function parseUserAgent(userAgent?: string): LowEntropyBrowserHints {
  const platform = parsePlatform(userAgent?.match(platformRegex)?.[0]);
  const browsers = parseBrowserBrandsFromUa(userAgent);
  return {
    browsers: browsers as { brand: BrowserBrand; majorVersion: number }[],
    isMobile: userAgent ? isMobileRegex.test(userAgent) : false,
    platform: platform ?? 'Unknown',
    saveData: false, // Not available from user agent. :(
  };
}
